import React, { useEffect, useState } from 'react';
import axios from '../../config/Axios';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '../../redux/users/actionCreator';
import { toast } from 'react-toastify';
import Navbar from '../Navbar';
import withAdminLayout from '../../layout/withAdminLayout';
import {
    connectAccountStatus,
    getConnectAccount,
    getPricingInfo,
    getDetails,
} from '../../redux/businessAccount/detailsAction';
import {
    cancelStripeDowngrade,
    cancelStripeSubscription,
    createStripeCheckoutSession,
    fetchStripeSubscriptionStatus,
    previewStripePlanChange,
    resetPreviewStripePlanChange,
    resumeStripeSubscription,
    connectStripeAccount,
    disconnectStripeAccount,
} from '../../redux/businessAccount/stripeAction';
import Loading from '../../components/Loading';
import ConnectAccountModal from './updateStripe';
import PlanChangePreviewModal from './planChangePreviewModal';
import DisconnectStripeModal from './disconnectStripeModal';
import ActionConfirmationModal from './actionConfirmationModal';
import moment from 'moment';

const Stripe = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const user = useSelector((state) => state.user.userData);
    console.log('user: ', user);
    const {
        pricing,
        isPriceLoading,
        accountStatus,
        connectAccount,
        subscriptionStatus,
        subscriptionStatusLoading,
        checkoutSessionLoading,
        checkoutSessionLoadingFor,
        planPreview,
        planPreviewLoading,
        resumeSubscriptionLoading,
        cancelDowngradeLoading,
        cancelSubscriptionLoading,
    } = useSelector((state) => state.businessDetails);
    const [dispatched, setDispatched] = useState(false);
    const [connectId, setConnectId] = useState('');
    const [connectLoading, setConnectLoading] = useState(false);
    const [disconnectLoading, setDisconnectLoading] = useState(false);
    const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPlanPreviewOpen, setIsPlanPreviewOpen] = useState(false);
    const [pendingPlanData, setPendingPlanData] = useState(null);
    const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] = useState(false);
    const [isResumeSubscriptionModalOpen, setIsResumeSubscriptionModalOpen] = useState(false);
    const [isCancelDowngradeModalOpen, setIsCancelDowngradeModalOpen] = useState(false);

    const handleDisconnectStripe = async () => {
        setDisconnectLoading(true);
        await dispatch(
            disconnectStripeAccount(() => {
                setDisconnectLoading(false);
                setIsDisconnectModalOpen(false);
                dispatch(getDetails());
            })
        );
        setDisconnectLoading(false);
    };

    const checkSubscription = (planId) => {
        if (!planId) return false;

        return currentPlan?.priceId === planId;
    };

    const getPlanByName = (name) =>
        pricing?.find((item) => item?.name === name);

    const isPlanActive = (priceId) => checkSubscription(priceId);

    const subscriptionInfo = subscriptionStatus || {};
    const currentPlan = subscriptionInfo?.currentPlan || null;
    const scheduledPlan = subscriptionInfo?.scheduledPlan || null;
    const planEndDate = subscriptionInfo?.periodEnd
        ? moment(subscriptionInfo?.periodEnd).format('DD MMM YYYY')
        : subscriptionInfo?.subscription?.expired_at
        ? moment
              .unix(subscriptionInfo?.subscription?.expired_at)
              .format('DD MMM YYYY')
        : 'N/A';
    const hasSubscription = Boolean(subscriptionInfo?.hasSubscription);
    const isDowngradeScheduled =
        subscriptionInfo?.status === 'downgrade_scheduled';
    const isCancelScheduled = subscriptionInfo?.status === 'cancel_scheduled';

    const isYearlyActive = () =>
        currentPlan?.name === 'Yearly' &&
        currentPlan?.priceId === getPlanByName('Yearly')?.priceId;

    const isMonthlyActive = () =>
        currentPlan?.name === 'Monthly' &&
        currentPlan?.priceId === getPlanByName('Monthly')?.priceId;

    const isPlanChosen = (planName) => currentPlan?.name === planName;
    const isCheckoutLoadingFor = (priceId) =>
        checkoutSessionLoading && checkoutSessionLoadingFor === priceId;

    const openPlanPreview = async (planKey, actionType) => {
        if (!user?.email) return;

        const nextPlan = getPlanByName(
            planKey === 'month' ? 'Monthly' : 'Yearly'
        );
        if (!nextPlan?.priceId) return;

        setPendingPlanData({
            email: user.email,
            priceId: planKey,
            action: actionType,
        });

        try {
            await dispatch(
                previewStripePlanChange({
                    email: user.email,
                    priceId: planKey,
                })
            );
            setIsPlanPreviewOpen(true);
        } catch (error) {
            console.error('Failed to preview plan change', error);
        }
    };

    const closePlanPreview = () => {
        setIsPlanPreviewOpen(false);
        setPendingPlanData(null);
        dispatch(resetPreviewStripePlanChange());
    };

    const confirmPlanPreview = async () => {
        if (!pendingPlanData) return;

        try {
            await handleCheckout(pendingPlanData);
            if (user?.email) {
                await dispatch(fetchStripeSubscriptionStatus(user.email));
            }
        } catch (error) {
            console.error('Failed to confirm plan change:', error);
        } finally {
            setIsPlanPreviewOpen(false);
            setPendingPlanData(null);
            dispatch(resetPreviewStripePlanChange());
        }
    };

    const getTopSubscriptionLabel = () => {
        if (subscriptionInfo?.status === 'no_subscription') {
            return 'Subscribe Now';
        }

        return 'Connected';
    };

    const handleTopSubscriptionAction = async () => {
        if (subscriptionInfo?.status === 'cancel_scheduled') {
            setIsResumeSubscriptionModalOpen(true);
            return;
        }

        if (subscriptionInfo?.status === 'downgrade_scheduled') {
            setIsCancelDowngradeModalOpen(true);
            return;
        }

        if (subscriptionInfo?.status === 'no_subscription') {
            await handleMonthlySubscription();
        }
    };

    const updateUser = (user) => {
        dispatch(setUser(user));
    };

    const handleMonthlySubscription = async () => {
        const priceItem = getPlanByName('Monthly');
        if (isPlanActive(priceItem?.priceId)) return;
        if (!priceItem?.priceId) return;
        if (hasSubscription) {
            await openPlanPreview('month');
        } else {
            await handleCheckout({ email: user?.email, priceId: 'month' });
        }
    };

    const handleAnnualSubscription = async () => {
        const priceItem = getPlanByName('Yearly');
        if (isPlanActive(priceItem?.priceId)) return;
        if (!priceItem?.priceId) return;
        if (hasSubscription) {
            await openPlanPreview('year');
        } else {
            await handleCheckout({ email: user?.email, priceId: 'year' });
        }
    };

    const handleDowngradeSubscription = async () => {
        if (!user?.email) return;
        await openPlanPreview('month', 'update_subscription');
    };

    const handleCancelDowngrade = async () => {
        try {
            await dispatch(cancelStripeDowngrade(user?.email));
            setIsCancelDowngradeModalOpen(false);
        } catch (error) {
            toast(
                error?.response?.data?.message || 'Unable to cancel downgrade'
            );
        }
    };

    const handleResumeSubscription = async () => {
        try {
            await dispatch(resumeStripeSubscription(user?.email));
            setIsResumeSubscriptionModalOpen(false);
        } catch (error) {
            toast(
                error?.response?.data?.message ||
                    'Unable to resume subscription'
            );
        }
    };

    const getSubscription = async (sessionId) => {
        const response = await axios.post('stripe/subscription', { sessionId });
        if (response.data) {
            await updateUserSubscription(response.data);
        }
    };

    const updateUserSubscription = async (data) => {
        try {
            const response = await axios.post(
                'user/update-user-subscription',
                data
            );
            console.log('In Success: Update user subscription', data);
            dispatch(setUser(response.data));
            if (response.data?.email) {
                dispatch(fetchStripeSubscriptionStatus(response.data.email));
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                    'Unable to subscribe this plan'
            );
            console.log('Error: update-user-subscription', error);
        }
    };
    const handleCheckout = async (data) => {
        try {
            await dispatch(
                createStripeCheckoutSession(data, (response) => {
                    if (response?.url) {
                        window.location = response.url;
                    }
                })
            );
        } catch (error) {
            toast(
                error?.response?.data?.message ||
                    'Unable create checkout session'
            );
            throw error;
        }
    };

    const handleConnect = async () => {
        if (user?.email) {
            await dispatch(connectStripeAccount(setConnectLoading));
        }
    };
    const handleCancelSubscription = async () => {
        try {
            await dispatch(cancelStripeSubscription(user?.email));
            setIsCancelSubscriptionModalOpen(false);
        } catch (error) {
            toast(
                error?.response?.data?.message ||
                    'Unable to cancel subscription'
            );
        }
    };

    useEffect(() => {
        const verifySession = async () => {
            const searchParams = new URLSearchParams(location.search);
            const session_id = searchParams.get('session_id');

            if (session_id) {
                await getSubscription(session_id);
                history.replace(location.pathname);
            }
        };
        verifySession();
    }, [location.search]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const success = searchParams.get('success');

        if (success && !dispatched) {
            const userDataString = localStorage.getItem('userData');
            const userData = JSON.parse(userDataString);

            setConnectId(userData?.stripe_connect_id);

            setDispatched(true);
        }
    }, [dispatch, dispatched]);

    useEffect(() => {
        const userDataString = localStorage.getItem('userData');
        const userData = JSON.parse(userDataString);

        if (connectId) {
            updateUser(userData);
            history.replace(location.pathname);
        }
    }, [connectId]);

    useEffect(() => {
        dispatch(getPricingInfo()).catch((err) =>
            console.error('Failed to get pricing info:', err)
        );
        dispatch(getDetails()).catch((err) =>
            console.error('Failed to get business details:', err)
        );
        if (user?.stripe_connect_id) {
            dispatch(connectAccountStatus(user?.stripe_connect_id)).catch(
                (err) =>
                    console.error('Failed to get connect account status:', err)
            );
            dispatch(getConnectAccount(user?.stripe_connect_id)).catch((err) =>
                console.error('Failed to get connect account:', err)
            );
        }
    }, [dispatch, user?.stripe_connect_id]);

    useEffect(() => {
        if (user?.email) {
            dispatch(fetchStripeSubscriptionStatus(user?.email)).catch((err) =>
                console.error('Failed to fetch subscription status:', err)
            );
        }
    }, [dispatch, user?.email]);

    const yearlyPlan = getPlanByName('Yearly');
    const monthlyPlan = getPlanByName('Monthly');

    const isStripeIncomplete =
        !user?.stripe_connect_charges_enabled &&
        accountStatus &&
        (accountStatus.status === 'restricted' ||
            (accountStatus.requirements &&
                accountStatus.requirements.length > 0));

    return (
        <div className={`container-fluid`}>
            {isPriceLoading ? (
                <Loading />
            ) : (
                <div className={`row`}>
                    <div
                        className={`col-12 px-0 bg-ff br-top-left-22 min-h-100vh`}
                    >
                        <div className={`container-fluid`}>
                            <Navbar />
                        </div>
                        <div className={`container-fluid`}>
                            <div
                                className={`row align-items-center justify-content-center pricing-section-row`}
                            >
                                <div
                                    className={`col-auto text-center pt-lg-0 pt-5`}
                                >
                                    <h6
                                        className={`m-0 pb-16 f-36 fw-normal poly c-0f151d pricing-hero-title`}
                                    >
                                        View & Access All Appointments
                                    </h6>
                                </div>
                            </div>
                            <div
                                className={`row align-items-center justify-content-center pricing-section-row`}
                            >
                                <div
                                    className={`col-md-4 col-12 text-center px-43`}
                                >
                                    <span
                                        className={`d-block f-14 fw-normal text-gray poppins pricing-hero-description`}
                                    >
                                        A payment plan has been carefully
                                        curated for you in order to be able to
                                        make and access all appointments from
                                        your clients
                                    </span>
                                </div>
                            </div>
                            <div className="row justify-content-center mt-3">
                                <div className="col-12 d-flex justify-content-center">
                                    <div
                                        className="card subscription-detail-card bg-ff p-4 w-100"
                                        style={{
                                            maxWidth: '50.625rem',
                                            marginBottom: '0',
                                        }}
                                    >
                                        <h6 className="detail-title m-0 pb-3">
                                            Payment Account
                                        </h6>
                                        <div className="p-3 rounded-4 bg-f8fafc d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3">
                                            <div className="d-flex align-items-start align-items-sm-center gap-3">
                                                {!user?.stripe_connect_charges_enabled ? (
                                                    <div
                                                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                        style={{
                                                            width: 42,
                                                            height: 42,
                                                            backgroundColor:
                                                                '#fef3f2',
                                                            color: '#d92d20',
                                                        }}
                                                    >
                                                        <svg
                                                            width="20"
                                                            height="20"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2.2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                                            <line
                                                                x1="12"
                                                                y1="9"
                                                                x2="12"
                                                                y2="13"
                                                            ></line>
                                                            <line
                                                                x1="12"
                                                                y1="17"
                                                                x2="12.01"
                                                                y2="17"
                                                            ></line>
                                                        </svg>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                        style={{
                                                            width: 42,
                                                            height: 42,
                                                            backgroundColor:
                                                                '#ecfdf3',
                                                            color: '#039855',
                                                        }}
                                                    >
                                                        <svg
                                                            width="20"
                                                            height="20"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <polyline points="20 6 9 17 4 12"></polyline>
                                                        </svg>
                                                    </div>
                                                )}
                                                <div>
                                                    <div
                                                        className="fw-semibold c-0f151d d-flex align-items-center gap-2"
                                                        style={{
                                                            fontSize: '15px',
                                                        }}
                                                    >
                                                        {!user?.stripe_connect_charges_enabled
                                                            ? isStripeIncomplete
                                                                ? 'Setup Incomplete'
                                                                : 'Not Connected'
                                                            : 'Connected'}
                                                    </div>
                                                    <div
                                                        className="text-gray mt-1"
                                                        style={{
                                                            fontSize: '13.5px',
                                                        }}
                                                    >
                                                        {!user?.stripe_connect_charges_enabled
                                                            ? isStripeIncomplete
                                                                ? 'Your Stripe account setup is incomplete. Your business is not live and will not appear in search results until you finish setting it up.'
                                                                : 'Connect your Stripe account. Your business is not live and will not appear in search results until your account is connected.'
                                                            : 'Your Stripe account is successfully connected and receiving payouts.'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="stripe-action-container d-flex align-items-center gap-2 mt-2 mt-sm-0">
                                                {!user?.stripe_connect_charges_enabled ? (
                                                    <button
                                                        className="btn btn-get-it-now-outline stripe-action-btn"
                                                        onClick={handleConnect}
                                                        disabled={
                                                            connectLoading
                                                        }
                                                    >
                                                        {connectLoading
                                                            ? 'Processing...'
                                                            : isStripeIncomplete
                                                            ? 'Resume Setup'
                                                            : 'Connect Stripe'}
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="btn btn-get-it-now-outline-danger stripe-action-btn"
                                                        onClick={() =>
                                                            setIsDisconnectModalOpen(
                                                                true
                                                            )
                                                        }
                                                    >
                                                        Disconnect
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {hasSubscription && isCancelScheduled && (
                                <div className="row justify-content-center mt-3">
                                    <div className="col-12 d-flex justify-content-center">
                                        <div
                                            className="subscription-banner subscription-banner--cancel subscription-banner--single"
                                            style={{ maxWidth: '50.625rem' }}
                                        >
                                            <div className="subscription-banner__notice">
                                                <div
                                                    className="subscription-banner__notice-icon"
                                                    aria-hidden="true"
                                                >
                                                    <svg
                                                        width="22"
                                                        height="22"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M12 8.75V12.25"
                                                            stroke="#B26A1C"
                                                            strokeWidth="1.8"
                                                            strokeLinecap="round"
                                                        />
                                                        <path
                                                            d="M12 15.75H12.01"
                                                            stroke="#B26A1C"
                                                            strokeWidth="2.2"
                                                            strokeLinecap="round"
                                                        />
                                                        <path
                                                            d="M10.29 4.86L2.82 17.5C2.49 18.08 2.32 18.66 2.32 19.19C2.32 20.53 3.41 21.62 4.75 21.62H19.25C20.59 21.62 21.68 20.53 21.68 19.19C21.68 18.66 21.51 18.08 21.18 17.5L13.71 4.86C13.05 3.74 11.95 3.74 11.29 4.86H10.29Z"
                                                            stroke="#B26A1C"
                                                            strokeWidth="1.5"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </div>
                                                <div className="subscription-banner__notice-text">
                                                    <div className="subscription-banner__notice-title">
                                                        Your subscription is
                                                        cancelled.
                                                    </div>
                                                    <div className="subscription-banner__notice-desc">
                                                        You'll keep access to
                                                        all appointments until{' '}
                                                        {planEndDate}, then it
                                                        ends - no further
                                                        charges.
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="subscription-banner__cta-row">
                                                <button
                                                    className="btn btn-get-it-now subscription-banner__action subscription-banner__action--left"
                                                    onClick={() => setIsResumeSubscriptionModalOpen(true)}
                                                    disabled={
                                                        resumeSubscriptionLoading
                                                    }
                                                >
                                                    {resumeSubscriptionLoading
                                                        ? 'Resuming...'
                                                        : 'Resume subscription'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {hasSubscription &&
                                !isCancelScheduled &&
                                !isDowngradeScheduled && (
                                    <div className="row justify-content-center mt-3">
                                        <div className="col-12 d-flex justify-content-center">
                                            <div
                                                className="card subscription-detail-card bg-ff p-4 w-100"
                                                style={{
                                                    maxWidth: '50.625rem',
                                                    marginBottom: '0',
                                                }}
                                            >
                                                <div className="mb-3 p-3 rounded-4 bg-f8fafc d-flex align-items-center justify-content-between gap-3 flex-wrap">
                                                    <div>
                                                        <div className="fw-semibold c-0f151d">
                                                            Subscription active
                                                        </div>
                                                        <div className="text-gray f-14">
                                                            Manage your plan or
                                                            cancel anytime from
                                                            here.
                                                        </div>
                                                    </div>
                                                    <button
                                                        className="btn btn-get-it-now-outline"
                                                        onClick={() => setIsCancelSubscriptionModalOpen(true)}
                                                        disabled={
                                                            cancelSubscriptionLoading
                                                        }
                                                    >
                                                        {cancelSubscriptionLoading
                                                            ? 'Cancelling...'
                                                            : 'Cancel Subscription'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            <div className="row justify-content-center mt-3 mb-4">
                                <div className="col-12 d-flex justify-content-center">
                                    <div
                                        className="card subscription-detail-card bg-ff p-4 w-100"
                                        style={{
                                            maxWidth: '50.625rem',
                                            marginBottom: '0',
                                        }}
                                    >
                                        <h6 className="detail-title m-0 pb-20">
                                            Subscription details
                                        </h6>
                                        <div className="row text-start align-items-start">
                                            <div
                                                className="col-md-3 col-sm-6 col-12 mb-3 mb-md-0 text-left"
                                                style={{ textAlign: 'left' }}
                                            >
                                                <span className="detail-label d-block">
                                                    Current Plan
                                                </span>
                                                <span className="detail-value d-block">
                                                    {currentPlan?.name ||
                                                        subscriptionInfo
                                                            ?.subscription
                                                            ?.plan_id ||
                                                        'N/A'}
                                                </span>
                                            </div>
                                            <div
                                                className="col-md-3 col-sm-6 col-12 mb-3 mb-md-0 text-left pe-md-3"
                                                style={{ textAlign: 'left' }}
                                            >
                                                <span className="detail-label d-block">
                                                    Status
                                                </span>
                                                <div className="subscription-status-wrap">
                                                    <span
                                                        className="status-dot"
                                                        style={{ margin: 0 }}
                                                    ></span>
                                                    <span
                                                        className="status-text"
                                                        style={{ margin: 0 }}
                                                    >
                                                        {subscriptionInfo?.status ===
                                                        'cancel_scheduled'
                                                            ? 'Cancel scheduled'
                                                            : subscriptionInfo?.status ===
                                                              'downgrade_scheduled'
                                                            ? 'Downgrade scheduled'
                                                            : subscriptionInfo?.status ===
                                                              'no_subscription'
                                                            ? 'No subscription'
                                                            : 'Active'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div
                                                className="col-md-3 col-sm-6 col-12 mb-3 mb-md-0 text-left ps-md-2"
                                                style={{ textAlign: 'left' }}
                                            >
                                                <span className="detail-label d-block">
                                                    Next Billing Date
                                                </span>
                                                <span className="detail-value d-block">
                                                    {subscriptionInfo?.periodEnd
                                                        ? moment(
                                                              subscriptionInfo?.periodEnd
                                                          ).format(
                                                              'DD MMM YYYY'
                                                          )
                                                        : subscriptionInfo
                                                              ?.subscription
                                                              ?.expired_at
                                                        ? moment
                                                              .unix(
                                                                  subscriptionInfo
                                                                      ?.subscription
                                                                      ?.expired_at
                                                              )
                                                              .format(
                                                                  'DD MMM YYYY'
                                                              )
                                                        : 'N/A'}
                                                </span>
                                            </div>
                                            <div
                                                className="col-md-3 col-sm-6 col-12 text-left"
                                                style={{ textAlign: 'left' }}
                                            >
                                                <span className="detail-label d-block">
                                                    Scheduled Change
                                                </span>
                                                {scheduledPlan ? (
                                                    <>
                                                        <span className="change-text d-block">
                                                            {
                                                                scheduledPlan?.name
                                                            }
                                                        </span>
                                                        <span className="change-subtext d-block">
                                                            Starts{' '}
                                                            {subscriptionInfo?.periodEnd
                                                                ? moment(
                                                                      subscriptionInfo?.periodEnd
                                                                  ).format(
                                                                      'MMM DD, YYYY'
                                                                  )
                                                                : 'N/A'}
                                                        </span>
                                                    </>
                                                ) : subscriptionInfo?.cancelAtPeriodEnd ? (
                                                    <>
                                                        <span
                                                            className="change-text d-block"
                                                            style={{
                                                                color: '#b54708',
                                                            }}
                                                        >
                                                            Cancel plan
                                                        </span>
                                                        <span className="change-subtext d-block">
                                                            Ends on{' '}
                                                            {subscriptionInfo?.periodEnd
                                                                ? moment(
                                                                      subscriptionInfo?.periodEnd
                                                                  ).format(
                                                                      'MMM DD, YYYY'
                                                                  )
                                                                : 'N/A'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span
                                                        className="detail-value text-muted d-block"
                                                        style={{
                                                            fontWeight:
                                                                'normal',
                                                            color: '#667085',
                                                        }}
                                                    >
                                                        None
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                className={`row align-items-center justify-content-center pb-100 ${
                                    hasSubscription ? '' : 'pt-80'
                                }`}
                            >
                                <div className={`col-md-auto col-12 px-2 pb-3`}>
                                    <div
                                        className={`card price-card price-card--featured${
                                            isPlanChosen('Yearly')
                                                ? ' price-card--active'
                                                : ''
                                        } bg-ff br-top-left-22 mx-auto`}
                                    >
                                        {isPlanChosen('Yearly') && (
                                            <span className="plan-badge plan-badge--active">
                                                Active Now
                                            </span>
                                        )}
                                        <div className={`container-fluid`}>
                                            <div
                                                className={`row align-items-center pb-23`}
                                            >
                                                <div
                                                    className={`col-auto ps-0`}
                                                >
                                                    <h6
                                                        className={`f-28 poly fw-normal color-primary mb-0`}
                                                    >
                                                        Yearly
                                                    </h6>
                                                </div>
                                                <div
                                                    className={`col-auto px-0`}
                                                >
                                                    <h6
                                                        className={`recommended m-0`}
                                                    >
                                                        Recommended
                                                    </h6>
                                                </div>
                                            </div>

                                            <div
                                                className={`row align-items-center pb-16 border-b-1-ddd`}
                                            >
                                                <div
                                                    className={`col-auto ps-0`}
                                                >
                                                    <h6
                                                        className={`f-14 poppins fw-normal c-667085 mb-0`}
                                                    >
                                                        A payment plan has been
                                                        carefully curated for
                                                        you in order to be able
                                                        to make
                                                    </h6>
                                                </div>
                                            </div>

                                            <div
                                                className={`row align-items-center pt-24 pb-16 border-b-1-ddd`}
                                            >
                                                <div
                                                    className={`col-auto ps-0 pe-2`}
                                                >
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M6 12.75L9.75 16.5L18 7.5"
                                                            stroke="#FFC931"
                                                            strokeWidth="1.5"
                                                        />
                                                    </svg>
                                                </div>
                                                <div className={`col-10 ps-0`}>
                                                    <h6
                                                        className={`f-12 poppins fw-normal c-667085 mb-0`}
                                                    >
                                                        View All Appointments
                                                    </h6>
                                                </div>
                                                <div
                                                    className={`col-auto ps-0 pe-2`}
                                                >
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M6 12.75L9.75 16.5L18 7.5"
                                                            stroke="#FFC931"
                                                            strokeWidth="1.5"
                                                        />
                                                    </svg>
                                                </div>
                                                <div className={`col-10 ps-0`}>
                                                    <h6
                                                        className={`f-12 poppins fw-normal c-667085 mb-0`}
                                                    >
                                                        Get Notifications
                                                    </h6>
                                                </div>
                                                <div
                                                    className={`col-auto ps-0 pe-2`}
                                                >
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M6 12.75L9.75 16.5L18 7.5"
                                                            stroke="#FFC931"
                                                            strokeWidth="1.5"
                                                        />
                                                    </svg>
                                                </div>
                                                <div className={`col-10 ps-0`}>
                                                    <h6
                                                        className={`f-12 poppins fw-normal c-667085 mb-0`}
                                                    >
                                                        Automated preferences
                                                    </h6>
                                                </div>
                                            </div>
                                            <div
                                                className={`row align-items-center py-24`}
                                            >
                                                <div
                                                    className={`col-auto ps-0`}
                                                >
                                                    <h6
                                                        className={`f-24 poppins fw-medium color-primary d-flex align-items-center`}
                                                    >
                                                        £
                                                        {
                                                            pricing?.find(
                                                                (item) =>
                                                                    item?.name ===
                                                                    'Yearly'
                                                            )?.price
                                                        }
                                                        <span
                                                            className={`f-12 poppins c-667085`}
                                                        >
                                                            /year
                                                        </span>
                                                    </h6>
                                                </div>
                                            </div>
                                        </div>
                                        {isPlanChosen('Yearly') ? (
                                            <button
                                                className={`btn btn-get-it-now btn-get-it-now--active`}
                                                disabled
                                            >
                                                Annual £
                                                {yearlyPlan?.price ?? ''}
                                            </button>
                                        ) : isPlanChosen('Monthly') ? (
                                            <button
                                                className={`btn btn-get-it-now`}
                                                onClick={
                                                    handleAnnualSubscription
                                                }
                                                disabled={isCheckoutLoadingFor(
                                                    'year'
                                                )}
                                            >
                                                {isCheckoutLoadingFor('year')
                                                    ? 'Processing...'
                                                    : 'Upgrade'}
                                            </button>
                                        ) : (
                                            <button
                                                className={`btn btn-get-it-now`}
                                                onClick={
                                                    handleAnnualSubscription
                                                }
                                                disabled={isCheckoutLoadingFor(
                                                    'year'
                                                )}
                                            >
                                                {isCheckoutLoadingFor('year')
                                                    ? 'Processing...'
                                                    : `Annual £${
                                                          yearlyPlan?.price ??
                                                          ''
                                                      }`}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className={`col-md-auto col-12 px-2 pb-3`}>
                                    <div
                                        className={`card price-card${
                                            isPlanChosen('Monthly')
                                                ? ' price-card--active'
                                                : ''
                                        } bg-ff br-top-left-22 mx-auto`}
                                    >
                                        {isPlanChosen('Monthly') && (
                                            <span className="plan-badge plan-badge--active">
                                                Active Now
                                            </span>
                                        )}
                                        {isDowngradeScheduled && (
                                            <span className="plan-badge plan-badge--warning">
                                                Downgraded
                                            </span>
                                        )}
                                        <div className={`container-fluid`}>
                                            <div
                                                className={`row align-items-center pb-23`}
                                            >
                                                <div
                                                    className={`col-auto ps-0`}
                                                >
                                                    <h6
                                                        className={`f-28 poly fw-normal color-primary mb-0`}
                                                    >
                                                        Monthly
                                                    </h6>
                                                </div>
                                            </div>
                                            <div
                                                className={`row align-items-center pb-16 border-b-1-ddd`}
                                            >
                                                <div
                                                    className={`col-auto ps-0`}
                                                >
                                                    <h6
                                                        className={`f-14 poppins fw-normal c-667085 mb-0`}
                                                    >
                                                        A payment plan has been
                                                        carefully curated for
                                                        you in order to be able
                                                        to make
                                                    </h6>
                                                </div>
                                            </div>
                                            <div
                                                className={`row align-items-center pt-24 pb-16 border-b-1-ddd`}
                                            >
                                                <div
                                                    className={`col-auto ps-0 pe-2`}
                                                >
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M6 12.75L9.75 16.5L18 7.5"
                                                            stroke="#FFC931"
                                                            strokeWidth="1.5"
                                                        />
                                                    </svg>
                                                </div>
                                                <div className={`col-10 ps-0`}>
                                                    <h6
                                                        className={`f-12 poppins fw-normal c-667085 mb-0`}
                                                    >
                                                        View All Appointments
                                                    </h6>
                                                </div>
                                                <div
                                                    className={`col-auto ps-0 pe-2`}
                                                >
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M6 12.75L9.75 16.5L18 7.5"
                                                            stroke="#FFC931"
                                                            strokeWidth="1.5"
                                                        />
                                                    </svg>
                                                </div>
                                                <div className={`col-10 ps-0`}>
                                                    <h6
                                                        className={`f-12 poppins fw-normal c-667085 mb-0`}
                                                    >
                                                        Get Notifications
                                                    </h6>
                                                </div>
                                                <div
                                                    className={`col-auto ps-0 pe-2`}
                                                >
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M6 12.75L9.75 16.5L18 7.5"
                                                            stroke="#FFC931"
                                                            strokeWidth="1.5"
                                                        />
                                                    </svg>
                                                </div>
                                                <div className={`col-10 ps-0`}>
                                                    <h6
                                                        className={`f-12 poppins fw-normal c-667085 mb-0`}
                                                    >
                                                        Automated preferences
                                                    </h6>
                                                </div>
                                            </div>
                                            <div
                                                className={`row align-items-center py-24`}
                                            >
                                                <div
                                                    className={`col-auto ps-0`}
                                                >
                                                    <h6
                                                        className={`f-24 poppins fw-medium color-primary d-flex align-items-center`}
                                                    >
                                                        £
                                                        {
                                                            pricing?.find(
                                                                (item) =>
                                                                    item?.name ===
                                                                    'Monthly'
                                                            )?.price
                                                        }
                                                        <span
                                                            className={`f-12 poppins c-667085`}
                                                        >
                                                            /month
                                                        </span>
                                                    </h6>
                                                </div>
                                            </div>
                                        </div>
                                        {isPlanChosen('Monthly') ? (
                                            <button
                                                className={`btn btn-get-it-now-outline btn-get-it-now-outline--active`}
                                                disabled
                                            >
                                                Monthly £
                                                {monthlyPlan?.price ?? ''}
                                            </button>
                                        ) : isDowngradeScheduled ? (
                                            <button
                                                className={`btn btn-get-it-now-outline`}
                                                onClick={() => setIsCancelDowngradeModalOpen(true)}
                                                disabled={
                                                    cancelDowngradeLoading
                                                }
                                            >
                                                {cancelDowngradeLoading
                                                    ? 'Cancelling...'
                                                    : 'Cancel Schedule'}
                                            </button>
                                        ) : isPlanChosen('Yearly') ||
                                          isYearlyActive() ? (
                                            <button
                                                className={`btn btn-get-it-now-outline`}
                                                onClick={
                                                    handleDowngradeSubscription
                                                }
                                                disabled={isCheckoutLoadingFor(
                                                    'month'
                                                )}
                                            >
                                                {isCheckoutLoadingFor('month')
                                                    ? 'Processing...'
                                                    : 'Downgrade'}
                                            </button>
                                        ) : (
                                            <button
                                                className={`btn btn-get-it-now-outline`}
                                                onClick={
                                                    handleMonthlySubscription
                                                }
                                                disabled={isCheckoutLoadingFor(
                                                    'month'
                                                )}
                                            >
                                                {isCheckoutLoadingFor('month')
                                                    ? 'Processing...'
                                                    : `Monthly £${
                                                          monthlyPlan?.price ??
                                                          ''
                                                      }`}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {isModalOpen && (
                <ConnectAccountModal
                    formData={accountStatus}
                    isModalOpen={isModalOpen}
                    handleClose={() => setIsModalOpen(false)}
                />
            )}
            <PlanChangePreviewModal
                isOpen={isPlanPreviewOpen}
                isLoading={planPreviewLoading}
                preview={planPreview}
                onClose={closePlanPreview}
                onConfirm={confirmPlanPreview}
                confirmLoading={checkoutSessionLoading}
            />
            {isDisconnectModalOpen && (
                <DisconnectStripeModal
                    isModalOpen={isDisconnectModalOpen}
                    handleClose={() => setIsDisconnectModalOpen(false)}
                    handleConfirm={handleDisconnectStripe}
                    isLoading={disconnectLoading}
                />
            )}
            <ActionConfirmationModal
                isModalOpen={isCancelSubscriptionModalOpen}
                handleClose={() => setIsCancelSubscriptionModalOpen(false)}
                handleConfirm={handleCancelSubscription}
                isLoading={cancelSubscriptionLoading}
                title="Cancel Subscription?"
                description="Your subscription will be cancelled at the end of the current billing cycle. You will keep access to all features until then, and no further charges will be made."
                confirmText="Cancel Subscription"
                loadingText="Cancelling..."
                isDanger={true}
            />
            <ActionConfirmationModal
                isModalOpen={isResumeSubscriptionModalOpen}
                handleClose={() => setIsResumeSubscriptionModalOpen(false)}
                handleConfirm={handleResumeSubscription}
                isLoading={resumeSubscriptionLoading}
                title="Resume Subscription?"
                description="This will reactivate your scheduled subscription. Your billing cycle will continue as normal without interruption."
                confirmText="Resume Subscription"
                loadingText="Resuming..."
                isDanger={false}
            />
            <ActionConfirmationModal
                isModalOpen={isCancelDowngradeModalOpen}
                handleClose={() => setIsCancelDowngradeModalOpen(false)}
                handleConfirm={handleCancelDowngrade}
                isLoading={cancelDowngradeLoading}
                title="Cancel Plan Change?"
                description="This will cancel your scheduled downgrade. Your current plan and billing cycle will remain active as normal."
                confirmText="Cancel Schedule"
                loadingText="Cancelling..."
                isDanger={true}
            />
        </div>
    );
};

export default withAdminLayout(Stripe);
